<template>
  <div class="snow-select" :class="componentClasses">
    <snow-select-popper
      trigger="clickToToggle"
      :disabled="_disabled"
      :options="popperOptions"
    >
      <div
        slot="reference"
        ref="popperContainer"
        class="snow-select__container"
        :class="{ disabled: _disabled, 'no-value': !innerText }"
        :id="id"
      >
        <div class="d-flex flex-column w-100 align-items-start">
          <label v-if="label" :for="id" class="snow-select__label">
            {{ label }}
            <span v-if="required">*</span>
          </label>

          <input
            v-model="innerText"
            ref="input"
            type="text"
            readonly
            class="snow-select__inner"
          />
        </div>

        <i class="fs-16 ph ph-caret-up-down text-dark-40" />
      </div>

      <div class="snow-select__options" slot="default">
        <div v-if="loading" class="text-center pt-3">
          <b-spinner variant="primary" />
        </div>
        <span
          v-else-if="!options.length"
          class="snow-select__option-cell disabled"
        >
          Nenhuma opção disponível
        </span>

        <template v-else>
          <div
            v-for="option in options.filter(opt => !opt.hide)"
            :key="option.id"
          >
            <div
              class="snow-select__option-cell"
              :class="{
                disabled: option.disabled,
                selected: isSelected(option)
              }"
              @click="selectOption(option)"
            >
              <slot :name="option.id">
                <span>
                  {{ option.name || option.id }}
                </span>
              </slot>
            </div>
          </div>
        </template>
      </div>
    </snow-select-popper>

    <div v-if="errorMessage" class="snow-select__description-container">
      <div class="snow-select__description">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import Popper from "vue-popperjs";
import "vue-popperjs/dist/vue-popper.css";
export default {
  components: { "snow-select-popper": Popper },
  props: {
    value: {
      type: [String, Number, Array]
    },
    id: {
      type: String,
      default: "snow-select"
    },
    label: {
      type: String
    },
    required: {
      type: Boolean
    },
    errors: {
      type: [Array, String]
    },
    options: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      innerValue: this.value,
      innerText: "",
      accessed: false
    };
  },
  methods: {
    selectOption(option) {
      this.$emit("change", option);
      if (!option.disabled) {
        this.innerValue = option.id;
        this.setInnerText();
        this.$refs.popperContainer.click();
      }
    },
    isSelected(option) {
      return this.innerValue === option.id;
    },
    setInnerText() {
      this.innerText =
        this.options.find(opt => opt.id === this.innerValue)?.name || "";
    }
  },
  computed: {
    _disabled() {
      return this.disabled || this.loading;
    },
    errorMessage() {
      if (this.errors && this.errors.length) {
        if (_.isArray(this.errors)) return this.errors[0];
        return this.errors;
      }

      if (this.accessed && this.required && !this.innerValue)
        return "Esse campo é obrigatório.";

      return undefined;
    },
    popperOptions() {
      return {
        placement: "bottom",
        modifiers: {
          offset: {
            offset: "0,10px"
          }
        }
      };
    },
    componentClasses() {
      const classes = [];

      if (this.disabled) classes.push("disabled");
      if (this._disabled) classes.push("disabled");
      if (this.errorMessage) classes.push("error");

      return classes;
    }
  },
  watch: {
    value(newVal) {
      this.innerValue = newVal;
      this.setInnerText();
    },
    innerValue(newVal) {
      this.setInnerText();
      this.$emit("input", newVal);
    },
    options: {
      deep: true,
      handler() {
        this.setInnerText();
      }
    }
  },
  mounted() {
    this.setInnerText();
  }
};
</script>
<style lang="scss" scoped>
.snow-select {
  display: block;
  position: relative;

  .snow-select__container {
    display: flex;
    align-items: center;

    cursor: pointer;
    padding: 20px 20px 16px 20px;
    border-radius: 12px;
    background-color: var(--bg-5);
    transition: 0.2s all ease-in-out;
    border: 0.5px solid var(--dark-10);
    .snow-select__label {
      margin: 0px;
      font-size: 12px;
      line-height: 16px;
      color: var(--dark-40);
      transition: 0.2s all ease-in-out;
    }
    .snow-select__inner {
      border: none;
      outline: none;

      padding: 0;
      height: 20px;
      cursor: pointer;
      margin-top: 8px;
      visibility: visible;
      background: transparent;
      transition: all 0.2s ease-in-out;

      overflow: hidden;
      text-overflow: ellipsis;
    }
    &:not(.disabled) {
      &:hover {
        border: 0.5px solid var(--dark-40);
      }
      &:focus-within {
        border: 0.5px solid var(--dark-40);
        box-shadow: 0px 0px 0px 4px rgba(0, 0, 0, 0.05);
      }
      &:hover,
      &:focus-within {
        border: 0.5px solid var(--dark-40);
        &.no-value {
          .snow-select__label {
            color: var(--dark-80);
          }
        }
      }
    }
    &.no-value {
      padding: 18px 20px;
      .snow-select__inner {
        height: 0px;
        margin-top: 0px;
        visibility: hidden;
        transition: all 0.2s ease-in-out;
      }
      .snow-select__label {
        font-size: 16px;
        line-height: 21px;
        transition: 0.2s all ease-in-out;
      }
    }
  }

  .snow-select__options {
    width: 100%;
    padding: 8px;
    z-index: 100;
    overflow-y: auto;
    max-height: 250px;
    border-radius: 16px;
    border: 1px solid var(--dark-10);
    background: #fff;
    backdrop-filter: blur(12px);
    gap: 4px;
    display: flex;
    flex-direction: column;
    .snow-select__option-cell {
      border-radius: 4px;
      color: var(--dark-100);
      width: 100%;
      padding: 4px 12px;
      transition: 0.2s all;
      cursor: pointer;

      &.disabled {
        color: var(--dark-40);
        cursor: default;
      }

      &:not(.disabled) {
        &:hover {
          background-color: var(--dark-4);
        }
        &.selected {
          font-weight: 600;
          background-color: var(--dark-4);
        }
      }
    }
  }

  .snow-select__description-container {
    gap: 8px;
    display: flex;
    align-items: flex-start;
    .snow-select__description {
      flex: 1;
      color: #bf2e30;
    }
  }

  // States
  &.error {
    .snow-select__container {
      border-color: #ff5053;
    }
  }

  &.disabled {
    .snow-select__container {
      background-color: rgba(25, 25, 34, 0.0392156863);
      border-color: transparent;
      cursor: default;
      .snow-select__inner {
        color: $neutral-200;
        cursor: default;
      }
    }
  }
}
</style>
